import { byId, isAr, message, copyText, trackEvent } from './extra-common.js';

const input = byId('tc-input');
const output = byId('tc-output');
const copyBtn = byId('tc-copy');
const currencySelect = byId('tc-currency');

// Tafqeet Engine (Numbers to Arabic written words)
const TAFQEET = (() => {
  const ones = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة', 'عشرة',
    'أحد عشر', 'إثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
  const tens = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
  const hundreds = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

  const scales = [
    { singular: '', dual: '', plural: '' },
    { singular: 'ألف', dual: 'ألفان', plural: 'آلاف' },
    { singular: 'مليون', dual: 'مليونان', plural: 'ملايين' },
    { singular: 'مليار', dual: 'ملياران', plural: 'مليارات' }
  ];

  function convertChunk(n) {
    if (n === 0) return '';
    let res = '';
    const h = Math.floor(n / 100);
    const rem = n % 100;
    if (h > 0) res += hundreds[h];
    if (rem > 0) {
      if (res) res += ' و';
      if (rem < 20) {
        res += ones[rem];
      } else {
        const u = rem % 10;
        const t = Math.floor(rem / 10);
        if (u > 0) res += ones[u] + ' و' + tens[t];
        else res += tens[t];
      }
    }
    return res;
  }

  function numberToArabic(num) {
    if (num === 0) return 'صفر';
    let parts = [];
    let scaleIdx = 0;
    let n = Math.floor(num);

    while (n > 0) {
      const chunk = n % 1000;
      if (chunk > 0) {
        let chunkText = '';
        if (scaleIdx === 0) {
          chunkText = convertChunk(chunk);
        } else if (scaleIdx === 1 && chunk === 1) {
          chunkText = 'ألف';
        } else if (scaleIdx === 1 && chunk === 2) {
          chunkText = 'ألفان';
        } else if (scaleIdx > 1 && chunk === 1) {
          chunkText = scales[scaleIdx].singular;
        } else if (scaleIdx > 1 && chunk === 2) {
          chunkText = scales[scaleIdx].dual;
        } else if (chunk >= 3 && chunk <= 10) {
          chunkText = convertChunk(chunk) + ' ' + scales[scaleIdx].plural;
        } else {
          chunkText = convertChunk(chunk) + ' ' + scales[scaleIdx].singular;
        }
        parts.unshift(chunkText);
      }
      n = Math.floor(n / 1000);
      scaleIdx++;
    }
    return parts.join(' و');
  }

  const currencies = {
    SAR: { main: 'ريال سعودي', dual: 'ريالان سعوديان', plural: 'ريالات سعودية', sub: 'هللة', subPlural: 'هللات' },
    EGP: { main: 'جنيه مصري', dual: 'جنيهان مصريان', plural: 'جنيهات مصرية', sub: 'قرش', subPlural: 'قروش' },
    AED: { main: 'درهم إماراتي', dual: 'درهمان إماراتيان', plural: 'دراهم إماراتية', sub: 'فلس', subPlural: 'فلوس' },
    KWD: { main: 'دينار كويتي', dual: 'ديناران كويتيان', plural: 'دنانير كويتية', sub: 'فلس', subPlural: 'فلوس' },
    USD: { main: 'دولار أمريكي', dual: 'دولاران أمريكيان', plural: 'دولارات أمريكية', sub: 'سنت', subPlural: 'سنتات' },
    EUR: { main: 'يورو', dual: 'يورو', plural: 'يوروهات', sub: 'سنت', subPlural: 'سنتات' },
    NONE: { main: '', dual: '', plural: '', sub: '', subPlural: '' }
  };

  function tafqeet(val, curCode = 'SAR') {
    const raw = String(val).replace(/,/g, '').trim();
    const num = parseFloat(raw);
    if (isNaN(num)) return isAr ? 'يرجى إدخال رقم صحيح للتفقيط (مثال: 1250.50)' : 'Please enter a valid numeric value';

    const intPart = Math.floor(Math.abs(num));
    const decPart = Math.round((Math.abs(num) - intPart) * 100);
    const cur = currencies[curCode] || currencies.SAR;

    let res = numberToArabic(intPart);
    if (cur.main) {
      if (intPart === 1) res += ' ' + cur.main;
      else if (intPart === 2) res = cur.dual;
      else if (intPart >= 3 && intPart <= 10) res += ' ' + cur.plural;
      else res += ' ' + cur.main;
    }

    if (decPart > 0) {
      res += ' و' + numberToArabic(decPart);
      if (cur.sub) {
        res += (decPart >= 3 && decPart <= 10) ? (' ' + cur.subPlural) : (' ' + cur.sub);
      }
    }

    if (cur.main) {
      res = 'فقط ' + res + ' لا غير';
    }
    if (num < 0) {
      res = 'سالب ' + res;
    }
    return res;
  }

  return { tafqeet };
})();

function getWords(text) {
  return text.trim()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

function convert(mode) {
  trackEvent('tool_started', 'text-case-converter');
  try {
    const text = input ? input.value : '';
    const locale = isAr ? 'ar' : 'en';
    let result = text;

    switch (mode) {
      case 'upper':
        result = text.toLocaleUpperCase(locale);
        break;
      case 'lower':
        result = text.toLocaleLowerCase(locale);
        break;
      case 'title':
        result = text.toLocaleLowerCase(locale).replace(/(^|[\s\-_'’])([\p{L}\p{N}])/gu, (_, prefix, char) => prefix + char.toLocaleUpperCase(locale));
        break;
      case 'sentence':
        result = text.toLocaleLowerCase(locale).replace(/(^|[.!?؟\n]\s*)([\p{L}\p{N}])/gu, (_, prefix, char) => prefix + char.toLocaleUpperCase(locale));
        break;
      case 'clean':
        result = text.replace(/[\t\u00a0 ]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim();
        break;
      case 'slug':
        result = text.normalize('NFKD').toLocaleLowerCase(locale)
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\p{L}\p{N}]+/gu, '-')
          .replace(/^-+|-+$/g, '');
        break;
      case 'camel': {
        const words = getWords(text);
        result = words.map((w, idx) => {
          const lower = w.toLocaleLowerCase(locale);
          if (idx === 0) return lower;
          return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
        }).join('');
        break;
      }
      case 'snake': {
        const words = getWords(text);
        result = words.map((w) => w.toLocaleLowerCase(locale)).join('_');
        break;
      }
      case 'pascal': {
        const words = getWords(text);
        result = words.map((w) => {
          const lower = w.toLocaleLowerCase(locale);
          return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
        }).join('');
        break;
      }
      case 'kebab': {
        const words = getWords(text);
        result = words.map((w) => w.toLocaleLowerCase(locale)).join('-');
        break;
      }
      case 'constant': {
        const words = getWords(text);
        result = words.map((w) => w.toLocaleUpperCase(locale)).join('_');
        break;
      }
      case 'tashkeel':
        result = text.replace(/[\u064B-\u065F\u0670]/g, '');
        break;
      case 'normalize':
        result = text
          .replace(/[أإآٱ]/g, 'ا')
          .replace(/ى/g, 'ي')
          .replace(/ة/g, 'ه')
          .replace(/ؤ/g, 'و')
          .replace(/ئ/g, 'ي');
        break;
      case 'tatweel':
        result = text.replace(/\u0640+/g, '');
        break;
      case 'tafqeet': {
        const cur = currencySelect ? currencySelect.value : 'SAR';
        result = TAFQEET.tafqeet(text, cur);
        break;
      }
      default:
        break;
    }

    if (output) output.value = result;
    if (copyBtn) copyBtn.disabled = !result;
    message('tc-status', isAr ? 'تم التحويل والمعالجة بنجاح.' : 'Conversion complete.', 'success');
    trackEvent('tool_completed', 'text-case-converter');
  } catch {
    message('tc-status', isAr ? 'تعذر التحويل.' : 'Conversion failed.', 'error');
    trackEvent('tool_failed', 'text-case-converter');
  }
}

document.querySelectorAll('[data-case]').forEach((button) => {
  button.addEventListener('click', () => convert(button.dataset.case));
});

if (copyBtn) {
  copyBtn.addEventListener('click', async () => {
    try {
      if (output) {
        await copyText(output.value);
        message('tc-status', isAr ? 'تم النسخ بنجاح.' : 'Copied.', 'success');
        trackEvent('tool_completed', 'text-case-converter');
      }
    } catch {
      message('tc-status', isAr ? 'تعذر النسخ.' : 'Copy failed.', 'error');
      trackEvent('tool_failed', 'text-case-converter');
    }
  });
}
